import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command'
import { ChevronsUpDown, Check } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Progress } from '@/components/ui/progress'
import { Checkbox } from '@/components/ui/checkbox'
import { AlertCircle, CheckCircle2, Info, Loader2, Zap } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'

export default function SmokeTestShadCn() {
  const [progress] = useState(68)
  const [darkMode, setDarkMode] = useState(false)

  return (
    <div className={darkMode ? 'dark' : ''}>
      <div className="h-screen p-8 overflow-y-auto transition-colors duration-300 bg-background text-foreground">
        {/* Header */}
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">shadcn/ui Smoke Test</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Verifying all components render with brand tokens
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="dark-mode" className="text-sm">
                Dark mode
              </Label>
              <Switch id="dark-mode" checked={darkMode} onCheckedChange={setDarkMode} />
            </div>
          </div>

          <Separator />

          {/* Buttons */}
          <section className="space-y-3">
            <h2 className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
              Buttons
            </h2>
            <div className="flex flex-wrap gap-3">
              <Button>Primary</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="destructive">Destructive</Button>
              <Button disabled>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading
              </Button>
              <Button size="sm">Small</Button>
              <Button size="lg">Large</Button>
            </div>
          </section>

          <Separator />

          {/* Badges */}
          <section className="space-y-3">
            <h2 className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
              Badges
            </h2>
            <div className="flex flex-wrap gap-2">
              <Badge>Default</Badge>
              <Badge variant="secondary">Secondary</Badge>
              <Badge variant="outline">Outline</Badge>
              <Badge variant="destructive">Destructive</Badge>
            </div>
          </section>

          <Separator />

          {/* Alerts */}
          <section className="space-y-3">
            <h2 className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
              Alerts
            </h2>
            <div className="space-y-3">
              <Alert>
                <Info className="w-4 h-4" />
                <AlertTitle>Information</AlertTitle>
                <AlertDescription>
                  Tailwind + shadcn CSS variables are resolving correctly.
                </AlertDescription>
              </Alert>
              <Alert variant="destructive">
                <AlertCircle className="w-4 h-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>Something went wrong with the risk calculation.</AlertDescription>
              </Alert>
            </div>
          </section>

          <Separator />

          {/* Tabs + Card + Form */}
          <section className="space-y-3">
            <h2 className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
              Tabs & Cards
            </h2>
            <Tabs defaultValue="overview">
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="settings">Settings</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-primary" />
                        Risk Score
                      </CardTitle>
                      <CardDescription>Current portfolio risk assessment</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Overall</span>
                        <span className="font-semibold">{progress}%</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                      <div className="flex items-center gap-2">
                        <Avatar className="w-8 h-8">
                          <AvatarFallback className="text-xs">RM</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium">Rogerio Menezes</p>
                          <p className="text-xs text-muted-foreground">Risk Analyst</p>
                        </div>
                      </div>
                    </CardContent>
                    <CardFooter className="gap-2">
                      <Button size="sm" className="w-full">
                        View Report
                      </Button>
                      <Button size="sm" variant="outline" className="w-full">
                        Export
                      </Button>
                    </CardFooter>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Quick Analysis</CardTitle>
                      <CardDescription>Run a suitability check</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="client-id">Client ID</Label>
                        <Input id="client-id" placeholder="e.g. CLI-00421" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="portfolio">Portfolio</Label>
                        <Input id="portfolio" placeholder="e.g. Growth Balanced" />
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="include-esg" />
                        <Label htmlFor="include-esg" className="text-sm font-normal cursor-pointer">
                          Include ESG factors
                        </Label>
                      </div>
                    </CardContent>
                    <CardFooter>
                      <Button className="w-full">
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Run Check
                      </Button>
                    </CardFooter>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="details" className="mt-4">
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Details tab content goes here.</p>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="settings" className="mt-4">
                <Card>
                  <CardContent className="pt-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">Email notifications</p>
                        <p className="text-xs text-muted-foreground">
                          Receive alerts for risk threshold breaches
                        </p>
                      </div>
                      <Switch defaultChecked />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">Auto-refresh data</p>
                        <p className="text-xs text-muted-foreground">
                          Update risk scores every 5 minutes
                        </p>
                      </div>
                      <Switch />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </section>

          <Separator />

          {/* Sheet + Combobox */}
          <section className="space-y-3">
            <h2 className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
              Sheet & Combobox
            </h2>

            {/* Sheet */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline">Open Sheet</Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>Quick Panel</SheetTitle>
                </SheetHeader>
                <div className="mt-4 text-sm text-muted-foreground">
                  This is a test sheet component.
                </div>
              </SheetContent>
            </Sheet>

            {/* Combobox */}
            <ComboboxDemo />
          </section>

          {/* Token verification */}
          <section className="space-y-3">
            <h2 className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
              Brand Token Check
            </h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {[
                { label: '--primary', cls: 'bg-primary', text: 'text-primary-foreground' },
                { label: '--secondary', cls: 'bg-secondary', text: 'text-secondary-foreground' },
                { label: '--muted', cls: 'bg-muted', text: 'text-muted-foreground' },
                { label: '--accent', cls: 'bg-accent', text: 'text-accent-foreground' },
              ].map(({ label, cls, text }) => (
                <div key={label} className={`${cls} rounded-md p-3`}>
                  <p className={`${text} text-xs font-mono`}>{label}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}

function ComboboxDemo() {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState('')

  const frameworks = [
    { value: 'react', label: 'React' },
    { value: 'vue', label: 'Vue' },
    { value: 'angular', label: 'Angular' },
    { value: 'svelte', label: 'Svelte' },
  ]

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-[200px] justify-between">
          {value ? frameworks.find((f) => f.value === value)?.label : 'Select framework'}
          <ChevronsUpDown className="w-4 h-4 ml-2 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command>
          <CommandInput placeholder="Search..." />
          <CommandEmpty>No results.</CommandEmpty>
          <CommandGroup>
            {frameworks.map((framework) => (
              <CommandItem
                key={framework.value}
                value={framework.value}
                onSelect={(currentValue) => {
                  setValue(currentValue === value ? '' : currentValue)
                  setOpen(false)
                }}
              >
                <Check
                  className={`mr-2 h-4 w-4 ${value === framework.value ? 'opacity-100' : 'opacity-0'}`}
                />
                {framework.label}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
